import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  LabelList,
  Tooltip,
  ResponsiveContainer,
  Text
} from 'recharts';
import { PolicyChargeTypeChartData } from 'store/modules/Charts/types';
import { formatCurrency, formatPercent } from 'utils';
import { newPalette as colors, dummyData, emptyColor } from '../Utils';
import { TooltipCharges } from '../Tooltips';
import MorningstarCopyright from 'components/MorningstarCopyright/MorningstarCopyright';
import { radiusVertical } from 'utils/charts';

interface ExistingPoliciesProps {
  data: Array<PolicyChargeTypeChartData>;
  maxChargeValue: number;
  isAnimationActive?: boolean;
  height?: number;
}

interface TickProps {
  x: number;
  y: number;
  offset: number;
  payload: any;
  width: number;
  number?: string;
}

const Tick = ({ payload: { value }, ...rest }: any) => (
  <text {...rest} className="bar-chart-tick" dy={12}>
    {value}%
  </text>
);

const CustomizedAxisTick = ({ x, y, payload, number }: TickProps) => {
  const isOverall = payload.value.toLowerCase() === 'overall';
  return (
    <Text
      x={x}
      y={y}
      width={150}
      textAnchor="middle"
      verticalAnchor="start"
      style={{ fontWeight: isOverall ? '500' : '400', fontSize: '0.75rem' }}
    >
      {isOverall
        ? payload.value
        : number
          ? `${payload.value} (${number})`
          : payload.value}
    </Text>
  );
};

const ExistingPolicies = ({
  data,
  maxChargeValue,
  height = 400,
  isAnimationActive = true
}: ExistingPoliciesProps) => {
  const isDataProvided = data && data.length > 0;
  const dataToRender = isDataProvided ? data : dummyData.policyCharges;
  const adjustToPercentage = dataToRender.map(
    (data: PolicyChargeTypeChartData) => ({
      ...data,
      percentage_value: data.percentage_value * 100
    })
  );
  return (
    <>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={adjustToPercentage}
          margin={{
            top: 5,
            right: 80,
            left: isDataProvided ? 60 : -30,
            bottom: 40
          }}
        >
          <YAxis
            type="number"
            axisLine={isDataProvided}
            tickLine={false}
            tick={<Tick />}
            domain={[0, maxChargeValue]}
            allowDecimals={false}
          />
          {isDataProvided && <Tooltip content={TooltipCharges} />}
          <XAxis
            dataKey="type"
            type="category"
            axisLine={isDataProvided}
            tickLine={false}
            tick={(props: any) => {
              if (!isDataProvided) return <></>;
              const policyData = adjustToPercentage[props.index];
              return (
                <CustomizedAxisTick
                  x={props.x}
                  y={props.y}
                  payload={props.payload}
                  offset={props.offset}
                  width={props.width}
                  number={policyData.number}
                />
              );
            }}
            width={100}
          />

          <Bar
            dataKey="percentage_value"
            fill="#D95045"
            unit="%"
            maxBarSize={50}
            isAnimationActive={isAnimationActive}
            radius={radiusVertical}
          >
            {isDataProvided && (
              <LabelList
                dataKey="percentage_value"
                content={(val: any) => {
                  const isOverall = val.type.toLowerCase() === 'overall';
                  return (
                    <text
                      style={{
                        fontSize: '13px',
                        fontWeight: isOverall ? 500 : 400
                      }}
                      x={val.x}
                      y={
                        (val.value < 0
                          ? val.y + val.offset
                          : val.y - val.offset) - 16
                      }
                    >
                      <tspan>{formatPercent(val.value)}</tspan>
                    </text>
                  );
                }}
              />
            )}

            {isDataProvided && (
              <LabelList
                dataKey="cash_value"
                content={(val: any) => {
                  const isOverall = val.type.toLowerCase() === 'overall';
                  return (
                    <text
                      style={{
                        fontSize: '13px',
                        fontWeight: isOverall ? 500 : 400
                      }}
                      x={val.x}
                      y={
                        (val.value < 0
                          ? val.y + val.offset
                          : val.y - val.offset) - 3
                      }
                    >
                      <tspan>{formatCurrency(val.value)}</tspan>
                    </text>
                  );
                }}
              />
            )}
            {adjustToPercentage.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={isDataProvided ? colors[index] : emptyColor}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <MorningstarCopyright />
    </>
  );
};

export default ExistingPolicies;
